<template>
  <div>
    <header class="flex items-center justify-between gap-16 border-b-1 border-solid border-grey-divider-subdued p-16">
      <div class="flex min-w-0 flex-1 items-center gap-12">
        <Breadcrumb
          v-if="showBack"
          :isButton="true"
          ignore-query-return-path
          aria-label="Back to conversations"
          class="!mt-0 shrink-0 md:hidden"
          @click="$emit('back')"
        />
        <Avatar v-if="avatarName" :name="avatarName" :imageUrl="avatarUrl" size="base" />
        <div class="min-w-0">
          <router-link
            v-if="profileLink"
            class="primary-focus"
            :to="profileLink"
            target="_blank"
            rel="noopener noreferrer"
            @click.native="handleProfileClick"
          >
            <p class="text-m1 font-medium">{{ title }}</p>
          </router-link>
          <p v-else class="text-m1 font-medium">{{ title }}</p>
          <p v-if="subtitle" class="text-s text-grey-text-subdued">{{ subtitle }}</p>
        </div>
      </div>
      <div class="flex items-center gap-8">
        <div v-if="showScheduleInterview" class="px-8 py-4 rounded-md">
          <router-link :to="scheduleInterviewLink">
            <AddAvailabilityIcon :height="24" :width="22" color="#4B4C52" />
          </router-link>
        </div>
        <ChannelHeaderActions
          v-if="showKebabMenu"
          :correspondingUserAccountId="otherUserInformation?.correspondingUserAccountId || ''"
          :correspondingUserId="otherUserInformation?.correspondingUserId || ''"
          :accountType="accountType"
          :currentChannelUrl="currentChannelUrl"
        />
        <slot name="actions" />
      </div>
    </header>
    <div v-if="showTrustSafetyBanner">
      <AlertBanner :status="3">{{ otherUserInformation?.bannerMessage }}</AlertBanner>
    </div>
  </div>
</template>

<script lang="ts">
import type { PropType } from 'vue';
import Breadcrumb from '@/components/breadcrumb/Breadcrumb.vue';
import Avatar from '@/components/avatar/Avatar.vue';
import AlertBanner from '@/components/alert_banner/AlertBanner.vue';
import AddAvailabilityIcon from '@/graphics/icons/AddAvailabilityIcon.vue';
import ChannelHeaderActions from '@/apps/messages/components/channelHeader/ChannelHeaderActions.vue';
import { sendSegmentEvent } from '@/lib/segment';
import sitterDetailsForParent from '@/apps/messages/helpers/sitterDetails';
import getProviderProfileUrl from '@/helpers/providerProfileUrl';
import type { OtherUserInfo } from '@/apps/messages/helpers/otherUserInfo';

export default {
  name: 'ChannelHeader',
  components: {
    Breadcrumb,
    Avatar,
    AlertBanner,
    AddAvailabilityIcon,
    ChannelHeaderActions,
  },
  props: {
    title: {
      type: String,
      required: true,
    },
    subtitle: {
      type: String,
      default: '',
    },
    avatarName: {
      type: String,
      default: '',
    },
    avatarUrl: {
      type: String,
      default: '',
    },
    showBack: {
      type: Boolean,
      default: false,
    },
    currentChannelUrl: {
      type: String,
      default: '',
    },
    otherUserInformation: {
      type: Object as PropType<OtherUserInfo | null>,
      default: null,
    },
  },
  data(): { sitterHasVisibleProfile: boolean; sitterProviderType: string | null } {
    return {
      sitterHasVisibleProfile: false,
      sitterProviderType: null,
    };
  },
  computed: {
    accountType(): 'parent' | 'sitter' {
      return this.$store.getters['account/accountType'];
    },
    profileLink(): string | null {
      if (this.accountType === 'sitter' && !this.otherUserInformation?.accountClosed) {
        return `/open_jobs/${this.otherUserInformation?.correspondingUserAccountId}`;
      }
      if (this.accountType === 'parent' && !this.otherUserInformation?.accountClosed) {
        const providerType = this.sitterProviderType || this.otherUserInformation?.providerType;
        return getProviderProfileUrl(this.otherUserInformation?.correspondingUserId, null, providerType || null);
      }
      return null;
    },
    showTrustSafetyBanner(): boolean {
      return Boolean(this.otherUserInformation?.showTrustSafetyBanner);
    },
    showScheduleInterview(): boolean {
      return (
        this.accountType === 'parent' &&
        !this.otherUserInformation?.bannerMessage &&
        Boolean(this.otherUserInformation?.correspondingUserId) &&
        this.sitterHasVisibleProfile
      );
    },
    showKebabMenu(): boolean {
      return (
        Boolean(this.otherUserInformation?.correspondingUserId) &&
        Boolean(this.otherUserInformation?.correspondingUserAccountId) &&
        !this.otherUserInformation?.accountClosed
      );
    },
    scheduleInterviewLink(): string {
      return `/interview-request/${this.otherUserInformation?.correspondingUserId}/new`;
    },
  },
  watch: {
    otherUserInformation() {
      if (this.accountType === 'sitter') return;
      this.updateSitterDetails();
    },
  },
  mounted() {
    this.updateSitterDetails();
  },
  methods: {
    async updateSitterDetails() {
      if (this.accountType === 'sitter') return;
      if (!this.otherUserInformation?.correspondingUserId) return;
      const details = await sitterDetailsForParent(this.otherUserInformation.correspondingUserId);

      this.sitterHasVisibleProfile = details?.hasVisibleProfile;
      this.sitterProviderType = details?.providerType;
    },
    handleProfileClick() {
      if (this.accountType === 'parent') {
        sendSegmentEvent('Viewed Profile in Message', {
          viewed_profile_id: this.otherUserInformation?.correspondingUserId,
        });
      }
    },
  },
};
</script>
